// @givmo/app-admin — Settings → Privacy Settings tab (07-1.4).
//
// Two inner sub-tabs (Company profile / Activity feed), both reading off the same CompanyDetailedOut
// (no separate GET). Each radio AUTOSAVES on change — no Edit/Save button. The autosave is a TanStack
// OPTIMISTIC update with ROLLBACK on error + an error toast: on `onMutate` we snapshot the current
// company query data and write the chosen value in; on `onError` we restore the snapshot AND
// `toast.error`; on `onSettled` we invalidate the company query so the server value is re-read.
//
// ★ GUARDED rollback (Flutter `SettingsCubit` parity): `onError` reverts ONLY if the live cached
// value is still the value THIS mutation wrote (`context.attempted`). A failed save whose optimistic
// value has since been superseded by a newer concurrent toggle does NOT clobber that newer value.
//
// (This intentionally diverges from the Flutter silent-loss optimistic — a failed save here visibly
// rolls the radio back and tells the user, rather than leaving a stale optimistic value on screen.)
//
// TWO HOSTS, ONE COMPONENT PER SETTING (EP-MNT5(b), D-EP-48 ruling 3): the sections below are
// exported, and the company console's Company visibility card (`privacy-visibility-card.tsx`)
// renders the SAME sections — the same autosave, rollback, options and ids — as rows of one card on
// its composed Settings page (OM-17's two-views law). The card also renders the Dollar amounts
// section (EP-MNT16, D-EP-51), which the portal's tab does not carry: the console is its home.
//
// THE WORDS (EP-WEB30): the options' labels are DISPLAY words, never the wire values the backend's
// enums carry ("Not Visible", "Visible minus company name") — each option names its wire `value`
// and its own `label`, so the words can change while every request body stays byte-identical. The
// intro's note is D-EP-50 (9)'s, verbatim, in every state.
//
// WHO SEES CONTROLS (EP-WEB30's rider, cut 18's review F3): the three writers carry
// `company:edit_visibility` (EP-SUITE7). The portal's tab used to show every admin the radios, and a
// Member's change answered 403 and rolled back — a decorative control on a live surface. It now asks
// the standing read (`GET /admin/company/me`): a holder gets the controls as before; anyone else gets
// the current settings in words and "Ask an Owner to change these." (`mayEditVisibility`; a FAILED
// read fails open, the server being the backstop, exactly like the Payment Methods tab).

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  companiesGetCompanyAdminOptions,
  companiesGetCompanyQueryKey,
  companiesUpdateCompanyActivityFeedVisibilityMutation,
  companiesUpdateCompanyPublicAmountsMutation,
  companiesUpdateCompanyVisibilityMutation,
} from '@givmo/api-client/query';
import {
  isGivmoError,
  type CompanyActivityFeedVisibility,
  type CompanyDetailedOut,
  type CompanyPublicAmountsAudience,
  type CompanyVisibility,
} from '@givmo/api-client';
import {
  Card,
  CardBody,
  Checkbox,
  RadioTile,
  RadioTileGroup,
  Skeleton,
  Tabs,
  testIdProp,
  toast,
} from '@givmo/design-system';
import { mayEditVisibility } from './visibility-permissions';

const PREFIX = 'settings-privacy';

/** The tab's intro. The last sentence is D-EP-50 (9)'s note, verbatim, in every state. */
export const PRIVACY_INTRO =
  'These settings control who can see your company’s profile and activity in the Givmo app. We ' +
  'encourage as much visibility as you’re comfortable with, to make the most of community giving. ' +
  'Note that the app never shares your employees’ individual donation or match amounts.';

/** What someone without `company:edit_visibility` reads under the current settings. */
export const PRIVACY_ASK_AN_OWNER = 'Ask an Owner to change these.';

// ── Option words (EP-WEB30). `value` is the WIRE value, unchanged; `label` is what a person reads;
//    typographic curly quotes ’ “ ” intentional, not ASCII — a copy-drift test asserts one renders ─
interface RadioOption<T extends string> {
  value: T;
  label: string;
  description: string;
  testId: string;
}

export const COMPANY_OPTIONS: RadioOption<CompanyVisibility>[] = [
  {
    value: 'Visible',
    label: 'Visible',
    description:
      'Any Givmo user can see your company’s profile in the Givmo app, so your employees can ' +
      'show pride in where they work.',
    testId: 'privacy-company-visible',
  },
  {
    value: 'Visible only to employees',
    label: 'Visible only to employees',
    description: 'Only your linked employees can see your company’s profile.',
    testId: 'privacy-company-only-employees',
  },
  {
    value: 'Not Visible',
    label: 'Not visible',
    description: 'No one on Givmo can see your company’s profile.',
    testId: 'privacy-company-not-visible',
  },
];

export const FEED_OPTIONS: RadioOption<CompanyActivityFeedVisibility>[] = [
  {
    value: 'Visible',
    label: 'Visible',
    description:
      'Matched donations say “+employer match” in the activity feed, and anyone who opens one can ' +
      'see that your company matched it.',
    testId: 'privacy-feed-visible',
  },
  {
    value: 'Visible minus company name',
    label: 'Visible without company name',
    description:
      'Matched donations still say “+employer match” in the activity feed, but your company’s name ' +
      'isn’t shown when someone opens one.',
    testId: 'privacy-feed-visible-minus-name',
  },
  {
    value: 'Visible only to employees',
    label: 'Visible only to employees',
    description:
      'Only your linked employees see the “Visible” view of matched donations in the activity feed.',
    testId: 'privacy-feed-only-employees',
  },
  {
    value: 'Not Visible',
    label: 'Not visible',
    description: 'Matched donations don’t say “+employer match” in the activity feed.',
    testId: 'privacy-feed-not-visible',
  },
];

// ── EP-MNT16 (D-EP-51): the Dollar amounts group's words. The audience's options run most visible
//    first, the order of the card's other two groups. ──────────────────────────────────────────────
export const AMOUNTS_OPTIONS: RadioOption<CompanyPublicAmountsAudience>[] = [
  {
    value: 'visible',
    label: 'Visible',
    description: 'Everyone who can see your profile sees its dollar amounts.',
    testId: 'privacy-amounts-visible',
  },
  {
    value: 'visible_to_employees',
    label: 'Visible only to employees',
    description: 'Only your linked employees see dollar amounts on your profile.',
    testId: 'privacy-amounts-only-employees',
  },
  {
    value: 'not_visible',
    label: 'Not visible',
    description: 'Your profile never shows dollar amounts.',
    testId: 'privacy-amounts-not-visible',
  },
];

export const DIRECT_GIFT_AMOUNTS_COPY = {
  // No-break spaces inside the parenthetical keep it whole on a narrow screen (Jonathan's words,
  // 2026-10-06; the 390 render wrapped "matches)" alone onto a third line without them).
  title:
    'Also show the amount of each gift your company makes directly (not\u00A0including\u00A0matches)',
} as const;

/** Why a total may not show: the k-anonymity floor (D-EP-57 (5)), said under the group while
 *  someone can see dollar amounts (Jonathan's words, cut 20's F3). */
export const AMOUNTS_FLOOR_NOTE =
  'To protect your employees’ privacy, totals show only when at least 5 employees’ matched gifts ' +
  'are in them, or none are.';

export interface SettingsPrivacyTabProps {
  company: CompanyDetailedOut;
}

type PrivacySubTab = 'company' | 'feed';

export function SettingsPrivacyTab({ company }: SettingsPrivacyTabProps) {
  const [subTab, setSubTab] = useState<PrivacySubTab>('company');
  // Who this admin is, and what they may do: the standing read the Payment Methods tab reads too.
  const standingQuery = useQuery(companiesGetCompanyAdminOptions());
  const canEdit = mayEditVisibility({
    settled: standingQuery.isSuccess,
    role: standingQuery.data?.admin_company_role ?? null,
    permissions: standingQuery.data?.permissions,
  });

  return (
    <div className="flex flex-col gap-16 pt-16" {...testIdProp(`${PREFIX}-tab`)}>
      <p className="font-sans text-s14 font-w400 leading-h15 tracking-normal text-text-secondary">
        {PRIVACY_INTRO}
      </p>

      {standingQuery.isPending ? (
        <div {...testIdProp(`${PREFIX}-standing-loading`)}>
          <Skeleton className="h-12 w-1/3" />
          <Skeleton className="mt-12 h-16 w-full" />
          <Skeleton className="mt-12 h-16 w-2/3" />
        </div>
      ) : canEdit ? (
        <Card>
          <CardBody>
            <Tabs.Root
              appearance="saas"
              value={subTab}
              onValueChange={(v) => setSubTab(v as PrivacySubTab)}
              testId={`${PREFIX}-subtabs`}
            >
              <Tabs.List aria-label="Privacy settings">
                <Tabs.Tab value="company" testId={`${PREFIX}-subtab-company`}>
                  Company profile
                </Tabs.Tab>
                <Tabs.Tab value="feed" testId={`${PREFIX}-subtab-feed`}>
                  Activity feed
                </Tabs.Tab>
              </Tabs.List>
              <Tabs.Panel value="company" testId={`${PREFIX}-panel-company`}>
                <CompanyVisibilitySection company={company} />
              </Tabs.Panel>
              <Tabs.Panel value="feed" testId={`${PREFIX}-panel-feed`}>
                <ActivityFeedSection company={company} />
              </Tabs.Panel>
            </Tabs.Root>
          </CardBody>
        </Card>
      ) : (
        <PrivacySettingsReadOnly company={company} />
      )}
    </div>
  );
}

/**
 * The current settings in words, for an admin who may not change them: each setting's chosen option
 * and what it means, then who can change them. Nothing here is a control.
 */
function PrivacySettingsReadOnly({ company }: { company: CompanyDetailedOut }) {
  const rows = [
    {
      key: 'company',
      title: 'Company profile',
      option: optionFor(COMPANY_OPTIONS, company.visibility ?? 'Not Visible'),
    },
    {
      key: 'feed',
      title: 'Activity feed',
      option: optionFor(FEED_OPTIONS, company.activity_feed_visibility ?? 'Not Visible'),
    },
  ];
  return (
    <Card testId={`${PREFIX}-readonly`}>
      <CardBody flush className="flex flex-col">
        {rows.map((row) => (
          <div
            key={row.key}
            className="flex flex-col gap-4 border-t border-border-subtle px-20 py-16 first:border-t-0"
            {...testIdProp(`${PREFIX}-readonly-${row.key}`)}
          >
            <h4 className="font-sans text-s13 font-w500 tracking-givmo text-text-tertiary">
              {row.title}
            </h4>
            <p
              className="font-sans text-s14 font-w600 tracking-givmo text-text-strong"
              {...testIdProp(`${PREFIX}-readonly-${row.key}-choice`)}
            >
              {row.option.label}
            </p>
            <p className="font-sans text-s14 font-w400 leading-h15 tracking-normal text-text-secondary">
              {row.option.description}
            </p>
          </div>
        ))}
        {/* The house note's type (the Payment Methods and Match Program notes): a quiet line, not a setting. */}
        <p
          className="border-t border-border-subtle px-20 py-16 font-sans text-s14 font-w400 leading-h15 tracking-normal text-text-tertiary"
          {...testIdProp(`${PREFIX}-readonly-ask`)}
        >
          {PRIVACY_ASK_AN_OWNER}
        </p>
      </CardBody>
    </Card>
  );
}

/**
 * The option a stored value names. A value the backend sent that this build does not know reads as
 * itself (no description): the words never claim a setting the company does not have.
 */
function optionFor<T extends string>(options: RadioOption<T>[], value: T): RadioOption<T> {
  return (
    options.find((option) => option.value === value) ?? {
      value,
      label: value,
      description: '',
      testId: '',
    }
  );
}

/**
 * How a host draws the sections: `'tab'` — the portal's Privacy tab, each description BESIDE its
 * tile at the tab's scale (the D-CPO-38 rider; the default, so the portal's markup never moves) — or
 * `'card'` — a card on the console's composed Settings page, in that page's own radio style: the
 * tile's own description (the Card-subtitle scale, part of the option it describes) and the page's
 * `gap-12`, the radio style that page's choices share (EP-MNT5; one page, one radio scale).
 */
export type PrivacySectionVariant = 'tab' | 'card';

// ── A radio group whose change OPTIMISTICALLY autosaves, rolling back + toasting on error ──────────
// The displayed selection comes off the company query; each section's mutation writes the optimistic
// value into that query (onMutate) and restores it on error (onError) — so the radio reflects the
// optimistic value immediately and visibly flips back on a failed save.
function VisibilityRadioSection<T extends string>({
  groupLabel,
  options,
  current,
  mutate,
  groupTestId,
  variant,
}: {
  groupLabel: string;
  options: RadioOption<T>[];
  /** The current value off the company query (with its own default applied). */
  current: T;
  /** Fire the optimistic save for a chosen value. */
  mutate: (value: T) => void;
  groupTestId: string;
  variant: PrivacySectionVariant;
}) {
  return (
    <RadioTileGroup
      value={current}
      onValueChange={(next) => mutate(next as T)}
      aria-label={groupLabel}
      className={variant === 'card' ? 'gap-12' : undefined}
      testId={groupTestId}
    >
      {options.map((opt) =>
        // The BARE e2e id sits on the interactive RadioTile (RadioTile forwards testId to its
        // Radio.Root) so the givmo-e2e-test spec, which clicks `opt.testId` to SELECT the radio,
        // lands on the actual control. The wrapper carries the `-row` id for layout-only queries.
        variant === 'card' ? (
          // `flex`, never a bare block: the tile's label is `inline-flex`, so in a block it sits on
          // a line box aligned by baseline, and a CHOSEN option (whose radio shows its dot, which
          // moves that baseline) grew its row by 4px — measured, cut 20 — uneven gaps in a group.
          <div key={opt.value} className="flex" {...testIdProp(`${opt.testId}-row`)}>
            <RadioTile
              value={opt.value}
              label={opt.label}
              description={opt.description}
              testId={opt.testId}
            />
          </div>
        ) : (
          <div key={opt.value} className="flex flex-col gap-2" {...testIdProp(`${opt.testId}-row`)}>
            <RadioTile value={opt.value} label={opt.label} testId={opt.testId} />
            <p className="ml-[30px] font-sans text-s14 font-w400 leading-h15 tracking-normal text-text-tertiary">
              {opt.description}
            </p>
          </div>
        ),
      )}
    </RadioTileGroup>
  );
}

export function CompanyVisibilitySection({
  company,
  variant = 'tab',
}: {
  company: CompanyDetailedOut;
  variant?: PrivacySectionVariant;
}) {
  const queryClient = useQueryClient();
  const current: CompanyVisibility = company.visibility ?? 'Not Visible';

  const mutation = useMutation({
    ...companiesUpdateCompanyVisibilityMutation(),
    onMutate: async (variables) => {
      const value = (variables as { body: { visibility: CompanyVisibility } }).body.visibility;
      const key = companiesGetCompanyQueryKey();
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<CompanyDetailedOut>(key);
      if (previous) {
        queryClient.setQueryData<CompanyDetailedOut>(key, { ...previous, visibility: value });
      }
      // `attempted` is what THIS mutation optimistically wrote — used to guard the rollback.
      return { previous, key, attempted: value };
    },
    onError: (error, _variables, context) => {
      // Guarded rollback (Flutter `SettingsCubit` parity): only revert if the live cached value is
      // STILL the value this mutation wrote — a newer concurrent toggle must not be clobbered.
      if (context?.previous) {
        const live = queryClient.getQueryData<CompanyDetailedOut>(context.key)?.visibility;
        if (live === context.attempted) {
          queryClient.setQueryData(context.key, context.previous);
        }
      }
      toast.error(
        isGivmoError(error) ? error.message : 'Could not update company visibility. Please retry.',
      );
    },
    onSettled: (_d, _e, _v, context) => {
      void queryClient.invalidateQueries({
        queryKey: context?.key ?? companiesGetCompanyQueryKey(),
      });
    },
  });

  return (
    <VisibilityRadioSection<CompanyVisibility>
      groupLabel="Company profile visibility"
      options={COMPANY_OPTIONS}
      current={current}
      mutate={(value) => mutation.mutate({ body: { visibility: value } })}
      groupTestId={`${PREFIX}-company-group`}
      variant={variant}
    />
  );
}

export function ActivityFeedSection({
  company,
  variant = 'tab',
}: {
  company: CompanyDetailedOut;
  variant?: PrivacySectionVariant;
}) {
  const queryClient = useQueryClient();
  const current: CompanyActivityFeedVisibility = company.activity_feed_visibility ?? 'Not Visible';

  const mutation = useMutation({
    ...companiesUpdateCompanyActivityFeedVisibilityMutation(),
    onMutate: async (variables) => {
      const value = (variables as { body: { visibility: CompanyActivityFeedVisibility } }).body
        .visibility;
      const key = companiesGetCompanyQueryKey();
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<CompanyDetailedOut>(key);
      if (previous) {
        queryClient.setQueryData<CompanyDetailedOut>(key, {
          ...previous,
          activity_feed_visibility: value,
        });
      }
      // `attempted` is what THIS mutation optimistically wrote — used to guard the rollback.
      return { previous, key, attempted: value };
    },
    onError: (error, _variables, context) => {
      // Guarded rollback (Flutter `SettingsCubit` parity): only revert if the live cached value is
      // STILL the value this mutation wrote — a newer concurrent toggle must not be clobbered.
      if (context?.previous) {
        const live = queryClient.getQueryData<CompanyDetailedOut>(
          context.key,
        )?.activity_feed_visibility;
        if (live === context.attempted) {
          queryClient.setQueryData(context.key, context.previous);
        }
      }
      toast.error(
        isGivmoError(error)
          ? error.message
          : 'Could not update activity feed visibility. Please retry.',
      );
    },
    onSettled: (_d, _e, _v, context) => {
      void queryClient.invalidateQueries({
        queryKey: context?.key ?? companiesGetCompanyQueryKey(),
      });
    },
  });

  return (
    <VisibilityRadioSection<CompanyActivityFeedVisibility>
      groupLabel="Activity feed visibility"
      options={FEED_OPTIONS}
      current={current}
      mutate={(value) => mutation.mutate({ body: { visibility: value } })}
      groupTestId={`${PREFIX}-feed-group`}
      variant={variant}
    />
  );
}

/** The two dollar-amounts settings, stated whole on every save (the writer takes both). */
interface PublicAmountsSettings {
  public_amounts_audience: CompanyPublicAmountsAudience;
  public_direct_gift_amounts: boolean;
}

/**
 * EP-MNT16 (D-EP-51) — the company profile's dollar amounts: who may see them (the audience), and,
 * while anyone may, whether each gift the company makes directly shows its own amount. An employee's
 * gift or match never shows one, whatever is chosen here, and the group's hint says so; the note
 * under the checkbox says why a total can be missing (the k-anonymity floor, D-EP-57 (5)).
 *
 * One writer (`PUT /admin/company/settings/public_amounts`) takes BOTH settings on every save, so a
 * change to one restates the other as the company query holds it — optimistic values included, so a
 * quick second change never undoes the first. The same autosave and guarded rollback as the other
 * sections: a failed save reverts only while the cache still holds exactly what it wrote.
 *
 * The checkbox and the note show only while the audience lets someone see dollar amounts: under
 * "Not visible" the checkbox would change nothing anyone sees (a decorative control) and the note
 * would explain figures no one is shown. The stored choice is kept either way, so it comes back as it
 * was when the audience opens again.
 */
export function PublicAmountsSection({ company }: { company: CompanyDetailedOut }) {
  const queryClient = useQueryClient();
  const current: PublicAmountsSettings = {
    public_amounts_audience: company.public_amounts_audience ?? 'not_visible',
    public_direct_gift_amounts: company.public_direct_gift_amounts ?? false,
  };

  const mutation = useMutation({
    ...companiesUpdateCompanyPublicAmountsMutation(),
    onMutate: async (variables) => {
      const value = (variables as { body: PublicAmountsSettings }).body;
      const key = companiesGetCompanyQueryKey();
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<CompanyDetailedOut>(key);
      if (previous) {
        queryClient.setQueryData<CompanyDetailedOut>(key, {
          ...previous,
          ...value,
          public_show_amounts: value.public_amounts_audience !== 'not_visible',
        });
      }
      // `attempted` is what THIS mutation optimistically wrote — used to guard the rollback.
      return { previous, key, attempted: value };
    },
    onError: (error, _variables, context) => {
      // Guarded rollback: only revert if the live cached pair is STILL the pair this mutation
      // wrote — a newer concurrent change must not be clobbered.
      if (context?.previous) {
        const live = queryClient.getQueryData<CompanyDetailedOut>(context.key);
        if (
          live?.public_amounts_audience === context.attempted.public_amounts_audience &&
          live?.public_direct_gift_amounts === context.attempted.public_direct_gift_amounts
        ) {
          queryClient.setQueryData(context.key, context.previous);
        }
      }
      toast.error(
        isGivmoError(error) ? error.message : 'Could not update dollar amounts. Please retry.',
      );
    },
    onSettled: (_d, _e, _v, context) => {
      void queryClient.invalidateQueries({
        queryKey: context?.key ?? companiesGetCompanyQueryKey(),
      });
    },
  });

  const save = (next: Partial<PublicAmountsSettings>) => {
    // Restate the pair from the LIVE cache (an earlier optimistic write included), then the change.
    const live = queryClient.getQueryData<CompanyDetailedOut>(companiesGetCompanyQueryKey());
    mutation.mutate({
      body: {
        public_amounts_audience: live?.public_amounts_audience ?? current.public_amounts_audience,
        public_direct_gift_amounts:
          live?.public_direct_gift_amounts ?? current.public_direct_gift_amounts,
        ...next,
      },
    });
  };
  const showsAmounts = current.public_amounts_audience !== 'not_visible';

  return (
    <div className="flex flex-col gap-16">
      <VisibilityRadioSection<CompanyPublicAmountsAudience>
        groupLabel="Who can see dollar amounts"
        options={AMOUNTS_OPTIONS}
        current={current.public_amounts_audience}
        mutate={(value) => save({ public_amounts_audience: value })}
        groupTestId={`${PREFIX}-amounts-group`}
        variant="card"
      />
      {showsAmounts && (
        // `gap-12` beside the 18px box: the same 30px label offset the radios' labels sit at.
        <>
          <label
            className="flex cursor-pointer items-start gap-12"
            {...testIdProp('privacy-amounts-direct-gifts')}
          >
            <Checkbox
              appearance="saas"
              checked={current.public_direct_gift_amounts}
              onCheckedChange={(checked) => save({ public_direct_gift_amounts: checked })}
              className="mt-2"
              testId="privacy-amounts-direct-gifts-checkbox"
            />
            <span
              className="min-w-0 font-sans text-s14 font-w500 tracking-givmo text-text-strong"
              {...testIdProp('privacy-amounts-direct-gifts-title')}
            >
              {DIRECT_GIFT_AMOUNTS_COPY.title}
            </span>
          </label>
          {/* A quiet note in the card's own tertiary scale (the group hint's and the descriptions'):
              the house note, not a setting. */}
          <p
            className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary"
            {...testIdProp('privacy-amounts-floor-note')}
          >
            {AMOUNTS_FLOOR_NOTE}
          </p>
        </>
      )}
    </div>
  );
}
